import React from 'react';
import { Button } from '@iterable/deprecated_react_components';
import noop from 'utils/noop';
import { PaginationContainer, Page } from './Pagination.styled';

// type Props = {
//   /** Additional Classes. */
//   className: string,
//   /** If buttons are iterable table style. */
//   isTableButtonStyle: boolean,
//   /** Max number of pages to show including first, last and overflow pages (Minimum: 7). */
//   maxPages: number,
//   /** Function to run when a page number is selected. */
//   onChangePage: (page: number) => void,
//   /** Number of pages. */
//   pages: number,
//   /** Currently selected page. */
//   page: number,
// };

export default class Pagination extends React.Component {
  static defaultProps = {
    maxPages: 7,
    onChangePage: noop,
    page: 1,
  };

  minPages = 7;
  overflowText = '...';

  /**
   * Determines the max number of pages to show. Max pages cannot be less than min pages (7).
   *
   * ```
   * getMaxPages()
   * ```
   * @returns { number } The max number of pages to show.
   */
  getMaxPages = () => {
    const { maxPages } = this.props;
    return maxPages >= this.minPages ? maxPages : this.minPages;
  };

  /**
   * Generates a page number or overflow page.
   *
   * ```
   * generatePage(page, key)
   * ```
   * @param { string | number } page: The page text to display.
   * @param { string } key: The key id of the page.
   * @returns { React.Component } The generated page number.
   */
  generatePage = (page, key) => {
    const { onChangePage, page: currentPage, isTableButtonStyle } = this.props;
    if (page === this.overflowText) {
      return (
        <Page
          $isOverflow
          data-test={`page-${page}`}
          data-role="overflow-page"
          key={`page-${page}-${key}`}
        >
          {page}
        </Page>
      );
    }
    const handleClick = (e) => {
      onChangePage(e, { page });
    };
    if (isTableButtonStyle) {
      return (
        <Button
          key={`page-${page}`}
          data-test={`page-${page}`}
          onClick={handleClick}
          selected={currentPage === page}
          table
        >
          {page}
        </Button>
      );
    }
    return (
      <Page
        key={`page-${page}`}
        data-test={`page-${page}`}
        onClick={handleClick}
        data-selected={currentPage === page}
      >
        {page}
      </Page>
    );
  };

  /**
   * Creates the pages of the pagination component.
   *
   * ```
   * generatePages()
   * ```
   * @returns { Array } Array of page number components.
   */
  generatePages = () => {
    const { pages, page } = this.props;
    const maxPages = this.getMaxPages();
    const padding = Math.floor(this.getMaxPages() / 2);
    const pageArray = [];
    const showAllPages = pages <= maxPages;

    let hasOverflowBefore = false;
    let hasOverflowAfter = false;
    let min;

    if (page - padding < 2 || showAllPages) {
      min = 1;
      hasOverflowAfter = maxPages < pages;
    } else if (page + padding >= pages) {
      min = pages - maxPages + 1;
      hasOverflowBefore = min > 1;
    } else {
      min = page - padding;
      hasOverflowBefore = true;
      hasOverflowAfter = true;
    }

    const numberOfPages = showAllPages ? pages : maxPages;

    for (let i = 0; i < numberOfPages; i += 1) {
      if (i === 0) {
        pageArray.push(this.generatePage(1, i));
      } else if (i === numberOfPages - 1) {
        pageArray.push(this.generatePage(pages, i));
      } else if ((i === 1 && hasOverflowBefore) || (i === numberOfPages - 2 && hasOverflowAfter)) {
        pageArray.push(this.generatePage(this.overflowText, i));
      } else {
        pageArray.push(this.generatePage(i + min, i));
      }
    }
    return pageArray;
  };

  render() {
    const { className, pages } = this.props;
    return (
      <PaginationContainer classname={className} data-role="pagination">
        {!!pages && this.generatePages()}
      </PaginationContainer>
    );
  }
}
